import { screeningsRoutes } from '@/backend/routes';
import { BackgroundCheckUserPanelHeader } from '@/ui/features/background-check/components/background-check-user-panel/components/BackgroundCheckUserPanelHeader';
import { useBackgroundCheckExpiringOnlyFilter } from '@/ui/features/background-check/hooks/useBackgroundCheckExpiringOnlyFilter';
import { useBackgroundCheckPanelColumns } from '@/ui/features/background-check/hooks/useBackgroundCheckTableColumns';
import { useBackgroundCheckTableFilterConfig } from '@/ui/features/background-check/hooks/useBackgroundCheckTableFilterConfig';
import { ParticipantDetailsComponent } from '@/ui/features/missions';
import { useSlideout } from '@/ui/hooks';
import { DataTable, UseDataTableFunction, useDataTableUsingUrl } from '@juvare/common-data-table-ui/DataTable';
import { FiltersBar } from '@juvare/common-data-table-ui/FiltersBar';
import { AccountColumns } from '@juvare/contact-management/backend/features/accounts/shared';
import { requireAnySystemPermission } from '@juvare/contact-management/backend/features/permissions/shared';
import {
    AccountFilterData,
    AccountListCustomBackendResponseData,
} from '@juvare/contact-management/ui/features/accounts/hooks/useAccountList';
import { useAccountsTable } from '@juvare/contact-management/ui/features/accounts/hooks/useAccountsTable';
import { getUserState } from '@juvare/platform-sdk-react/ui/stores';
import { Filter, Row } from '@juvare/platform-shared/features/storage';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { Box, FormControlLabel, Switch } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';

import { useBackgroundCheckExport } from '../../hooks/useBackgroundCheckExport';
export interface BackgroundCheckUserPanelProps {
    navigateBackTitle: string;
    onNavigateBack: () => void;
}

export const BackgroundCheckUserPanel: React.FC<BackgroundCheckUserPanelProps> = ({ navigateBackTitle, onNavigateBack }) => {
    const { t } = useTranslation();
    const { state } = useLocation();
    const userState = useSelector(getUserState);
    const [searchParams] = useSearchParams();
    const { handlePersonIdReset, handlePersonIdSelect, selectedPersonId, menuItems } = useSlideout();
    const requireOrgRoleInStatusFilter = !requireAnySystemPermission(userState, 'all-organizations-admin');
    const filterBarConfig = useBackgroundCheckTableFilterConfig(requireOrgRoleInStatusFilter);

    const { canEnableExpiringOnlyFilter, expiringOnlyFilters, isExpiringOnlyFilterChecked, handleShowExpiringOnlyFilterChange } =
        useBackgroundCheckExpiringOnlyFilter();

    const { dataTableProps, filtersBarProps, reload, clearActiveRow } = useAccountsTable({
        useDataTableFunction: useDataTableUsingUrl as UseDataTableFunction<
            AccountFilterData,
            Row<AccountColumns>,
            AccountListCustomBackendResponseData
        >,
        rowSelectionEnabled: true,
        requireOrgRoleInStatusFilter,
        staticStorageClientFilter: [
            { column: 'systemStatus', op: '=', value: 'active' },
            ...(expiringOnlyFilters as Filter<AccountColumns>[]),
        ],
        showAccountSystemRoleFilter: 'onlyAllFiltersPanel',
        showAccountStatusFilter: 'onlyAllFiltersPanel',
        onRowClick: (row) => {
            handlePersonIdSelect(row.id);
        },
    });
    const appliedStorageFilters = dataTableProps.customBackendResponseData?.storageClientRequest?.filter ?? [];
    const appliedOrderBy = dataTableProps.customBackendResponseData?.storageClientRequest?.orderBy ?? [];

    const { handleExportBackgroundCheck: handleExport } = useBackgroundCheckExport({
        filter:
            dataTableProps.selectedRowIds && dataTableProps.selectedRowIds.length > 0
                ? [
                      {
                          id: dataTableProps.selectedRowIds,
                          op: 'in',
                      },
                  ]
                : appliedStorageFilters,
        orderBy: appliedOrderBy,
    });
    const { handleExportBackgroundCheck: handleExportAll } = useBackgroundCheckExport({
        filter: [{ column: 'systemStatus', op: '=', value: 'active' }],
    });

    const id = searchParams.get('id');
    React.useEffect(() => {
        if (!selectedPersonId && id) {
            handlePersonIdSelect(id);
        }
    }, [handlePersonIdSelect, id, selectedPersonId]);

    const handleClearPersonId = () => {
        clearActiveRow();
        handlePersonIdReset();
    };

    dataTableProps.globalActions = [];
    dataTableProps.localActions = [];
    dataTableProps.filtersBar = <FiltersBar {...filtersBarProps} config={filterBarConfig} />;
    dataTableProps.columns = useBackgroundCheckPanelColumns({ reload });
    dataTableProps.title = t('pages.backgroundCheck.backgroundCheckTableTitle');

    if (!state?.backgroundCheckType) {
        return <Navigate to={`/${screeningsRoutes.root}`} replace />;
    }

    return (
        <>
            <BackgroundCheckUserPanelHeader
                backgroundCheckType={state.backgroundCheckType}
                navigateBackTitle={navigateBackTitle}
                onNavigateBack={onNavigateBack}
            />
            <Box p={2}>
                <DataTable
                    {...dataTableProps}
                    data-test='background-check-table'
                    customHeaderElement={
                        canEnableExpiringOnlyFilter && (
                            <FormControlLabel
                                sx={{ mt: -2, mb: -2 }}
                                label={t('pages.backgroundCheck.showExpiringOnly')}
                                control={<Switch checked={isExpiringOnlyFilterChecked} onChange={handleShowExpiringOnlyFilterChange} />}
                            />
                        )
                    }
                    globalActions={[
                        {
                            icon: <FileUploadOutlinedIcon />,
                            label: t('common.exportAll'),
                            onClick: handleExportAll,
                            'data-test': 'export-global-action-button',
                        },
                    ]}
                    localActions={[
                        {
                            icon: <FileUploadOutlinedIcon />,
                            label: t('common.export'),
                            onClick: handleExport,
                            'data-test': 'export-button',
                        },
                    ]}
                />
            </Box>
            <ParticipantDetailsComponent
                participantId={selectedPersonId}
                onParticipantIdReset={handleClearPersonId}
                slideoutMenuItems={menuItems}
            />
        </>
    );
};
